import lodashXor from 'lodash/fp/xor';

import { UTILITY_CONFIGURATION_SOURCE_DEFAULT } from 'client/constants/utilityConfigurationSource';

let latestBuildingUnitsPreviewInstanceId = 0;

const BUILDING_UNITS_REVIEW_REFRESH_MESSAGE =
  'The units found changed. Review the refreshed list before applying again.';
const BUILDING_UNITS_UPDATE_ERROR_MESSAGE =
  'This unit was saved, but we couldn’t update the other units. Try again.';

export const shouldRequestBuildingUnitsPreview = ({
  currentUtilityTypeIds,
  markAsReviewed,
  utilityTypeIds,
}) => {
  return markAsReviewed || lodashXor(utilityTypeIds, currentUtilityTypeIds).length > 0;
};

export const getBuildingUnitUpdateTargets = ({ siblingUnits, utilityTypeIds }) => {
  return siblingUnits.filter(({ currentUtilityTypeIds, utilityConfigurationProvenance }) => {
    const utilitiesChanged = lodashXor(utilityTypeIds, currentUtilityTypeIds).length > 0;
    const source = utilityConfigurationProvenance?.source;
    return utilitiesChanged || !source || source === UTILITY_CONFIGURATION_SOURCE_DEFAULT;
  });
};

export const getSelectedBuildingUnitTargets = ({
  excludedSiblingAddressIds = [],
  targetSiblingUnits,
}) => {
  const excludedSiblingAddressIdSet = new Set(excludedSiblingAddressIds);
  return targetSiblingUnits.filter(({ addressId }) => !excludedSiblingAddressIdSet.has(addressId));
};

export const getNextExcludedSiblingAddressIds = ({
  addressId,
  excludedSiblingAddressIds = [],
}) => {
  return excludedSiblingAddressIds.includes(addressId)
    ? excludedSiblingAddressIds.filter((excludedAddressId) => excludedAddressId !== addressId)
    : [...excludedSiblingAddressIds, addressId];
};

export const makeBuildingUnitsUpdatePayload = ({
  addressId,
  excludedSiblingAddressIds,
  siblingUnits,
  targetSiblingUnits,
  utilityPartnerId,
  utilityTypeIds,
}) => {
  const selectedSiblingUnits = getSelectedBuildingUnitTargets({
    excludedSiblingAddressIds,
    targetSiblingUnits,
  });
  if (selectedSiblingUnits.length === 0) {
    return null;
  }

  return {
    addressId,
    previewedSiblingAddressIds: siblingUnits.map(({ addressId: siblingAddressId }) =>
      siblingAddressId
    ),
    siblingAddressIds: selectedSiblingUnits.map(({ addressId: siblingAddressId }) =>
      siblingAddressId
    ),
    utilityPartnerId,
    utilityTypeIds,
  };
};

export const getNextBuildingUnitsPreviewInstanceId = () => {
  latestBuildingUnitsPreviewInstanceId += 1;
  return latestBuildingUnitsPreviewInstanceId;
};

export const makeBuildingUnitsPreviewQueryId = ({ addressId, instanceId, requestId }) => {
  return `${instanceId}-${addressId}-${requestId}`;
};

export const isLatestBuildingUnitsPreviewRequest = ({ completedRequestId, latestRequestId }) => {
  return completedRequestId === latestRequestId;
};

export const canDismissBuildingUnitsConfirmation = ({ isApplying, previewIsLoading }) => {
  return !isApplying || previewIsLoading;
};

export const isBuildingUnitsReviewConflict = (error) => error?.statusCode === 409;

export const handleBuildingUnitsApplyError = ({
  error,
  previewContext,
  requestPreview,
  setIsUpdating,
  setUpdateError,
}) => {
  if (isBuildingUnitsReviewConflict(error)) {
    return requestPreview(previewContext, {
      successMessage: BUILDING_UNITS_REVIEW_REFRESH_MESSAGE,
    });
  }

  setIsUpdating(false);
  setUpdateError(BUILDING_UNITS_UPDATE_ERROR_MESSAGE);
  return undefined;
};

export const makeBuildingUnitsPreviewLoadingState = (previewContext) => ({
  ...previewContext,
  excludedSiblingAddressIds: previewContext.excludedSiblingAddressIds || [],
  previewFailed: false,
  previewIsLoading: true,
  siblingUnits: [],
  targetSiblingUnits: [],
});

export const makeBuildingUnitsPreviewSuccessState = (
  previewContext,
  { buildingDisplay, reviewSource, siblingUnits = [] },
) => {
  if (siblingUnits.length === 0) {
    return null;
  }

  const targetSiblingUnits = getBuildingUnitUpdateTargets({
    siblingUnits,
    utilityTypeIds: previewContext.utilityTypeIds,
  });
  const targetSiblingAddressIdSet = new Set(
    targetSiblingUnits.map(({ addressId }) => addressId),
  );

  return {
    ...previewContext,
    buildingDisplay,
    excludedSiblingAddressIds: (previewContext.excludedSiblingAddressIds || []).filter(
      (addressId) => targetSiblingAddressIdSet.has(addressId),
    ),
    previewFailed: false,
    previewIsLoading: false,
    sourceUnit: {
      ...previewContext.sourceUnit,
      reviewSource,
    },
    siblingUnits,
    targetSiblingUnits,
  };
};

export const makeBuildingUnitsPreviewFailureState = (previewContext) => ({
  ...previewContext,
  excludedSiblingAddressIds: previewContext.excludedSiblingAddressIds || [],
  previewFailed: true,
  previewIsLoading: false,
  siblingUnits: [],
  targetSiblingUnits: [],
});
